// ============================================
// Creator Module - List Management (ES6)
// ============================================

import { showToast, escapeHtml } from '../utils.js';
import {
  getCurrentCreations, setCurrentCreations,
  getCurrentFilter, setCurrentFilter,
  getCurrentSort,
  getIsLoadingCreations, setIsLoadingCreations,
  getCreatorDb
} from './state.js';
import { getCurrentUser } from '../app/state.js';
import { getUserNickname } from '../auth/state.js';
import { showContentDetail } from '../home/detail/index.js';

// ============================================
// 제작물 목록 로드
// ============================================

export async function loadCreations(status = 'all') {
  const user = getCurrentUser();
  if (!user) {
    console.warn('로그인이 필요합니다');
    return;
  }

  // 이미 로딩 중이면 무시
  if (getIsLoadingCreations()) return;
  setIsLoadingCreations(true);

  setCurrentFilter(status);

  // 탭 활성화 상태 즉시 업데이트
  document.querySelectorAll('.creator-filter-btn').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.filter === status);
  });

  // 로딩 UI 표시
  const container = document.getElementById('creationsListContainer');
  if (container) {
    container.innerHTML = '<div class="creator-loading"><div class="spinner"></div></div>';
  }

  const db = getCreatorDb();

  try {
    // Firestore 직접 쿼리 - published 컬렉션에서 내 제작물 조회
    let query = db.collection('published')
      .where('creatorId', '==', user.uid);

    // status 필터 적용 (all이 아닌 경우)
    if (status && status !== 'all') {
      query = query.where('status', '==', status);
    }

    const snapshot = await query.orderBy('updatedAt', 'desc').get();

    const creations = [];
    snapshot.forEach(doc => {
      const data = doc.data();
      creations.push({
        id: doc.id,
        type: data.type || 'character',
        status: data.status || 'draft',
        title: data.title || '',
        imageUrl: data.imageUrl || data.character?.imageUrl || null,
        characterName: data.character?.name || '',
        shortDesc: data.shortDesc || '',
        shortIntro: data.shortIntro || data.shortDesc || '',
        tags: data.tags || data.hashtags || [],
        chatCount: data.chatCount || 0,
        likeCount: data.likeCount || 0,
        authorName: data.authorName || data.authorNickname || '',
        authorNickname: data.authorNickname || data.authorName || '',
        character: data.character || null,
        characters: data.characters || null,
        world: data.world || null,
        scenario: data.scenario || null,
        worldSetting: data.worldSetting || null,
        narrativeState: data.narrativeState || null,
        firstMessage: data.firstMessage || null,
        introImages: data.introImages || [],
        images: data.images || null,
        createdAt: data.createdAt?.toDate?.() || null,
        updatedAt: data.updatedAt?.toDate?.() || null
      });
    });

    setCurrentCreations(creations);
    renderCreationsList();
  } catch (error) {
    console.error('제작물 로드 오류:', error);
    showToast('제작물을 불러오는데 실패했습니다');
  } finally {
    setIsLoadingCreations(false);
  }
}

// ============================================
// 목록 렌더링
// ============================================

export function renderCreationsList() {
  const container = document.getElementById('creationsListContainer');
  if (!container) return;

  const currentFilter = getCurrentFilter();
  const currentSort = getCurrentSort();
  const currentCreations = getCurrentCreations();

  // 필터 버튼 상태 업데이트
  document.querySelectorAll('.creator-filter-btn').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.filter === currentFilter);
  });

  // 필터링
  let filtered = currentCreations;
  if (currentFilter !== 'all') {
    filtered = currentCreations.filter(c => c.status === currentFilter);
  }

  // 정렬
  filtered = [...filtered].sort((a, b) => {
    if (currentSort === 'plays') {
      return (b.chatCount || 0) - (a.chatCount || 0);
    }
    // recent (기본): 최신 수정순
    const aDate = a.updatedAt ? new Date(a.updatedAt) : new Date(0);
    const bDate = b.updatedAt ? new Date(b.updatedAt) : new Date(0);
    return bDate - aDate;
  });

  if (filtered.length === 0) {
    container.innerHTML = `
      <div class="creator-empty">
        <svg width="48" height="48" viewBox="0 0 256 256" fill="currentColor" opacity="0.3">
          <path d="M224,48H32A16,16,0,0,0,16,64V192a16,16,0,0,0,16,16H224a16,16,0,0,0,16-16V64A16,16,0,0,0,224,48ZM32,176V80H224v96Z"/>
          <circle cx="88" cy="128" r="24"/>
          <circle cx="168" cy="128" r="24"/>
        </svg>
        <p>${getEmptyMessage(currentFilter)}</p>
        <button class="creator-create-btn" data-action="openEditor">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <line x1="12" y1="4" x2="12" y2="20"/><line x1="4" y1="12" x2="20" y2="12"/>
          </svg>
          <span>점술가 만들기</span>
        </button>
      </div>
    `;
    return;
  }

  container.innerHTML = filtered.map(creation => renderCreationItem(creation)).join('');
}

function getEmptyMessage(filter) {
  switch (filter) {
    case 'public': return '공개된 점술가가 없습니다';
    case 'private': return '비공개 점술가가 없습니다';
    case 'draft': return '임시 저장된 점술가가 없습니다';
    default: return '아직 제작한 점술가가 없습니다';
  }
}

export function renderCreationItem(creation) {
  const statusLabel = getStatusLabel(creation.status);
  const statusClass = creation.status;
  const chatCount = creation.chatCount || 0;

  const safeTitle = escapeHtml(creation.title || '제목 없음');
  return `
    <div class="creator-item" data-id="${creation.id}" data-action="openDetail">
      <div class="creator-item-image">
        ${creation.imageUrl
          ? `<img src="${creation.imageUrl}" alt="${safeTitle}" onerror="if(this.parentElement)this.parentElement.innerHTML='<span>👤</span>'">`
          : '<span>👤</span>'
        }
      </div>
      <div class="creator-item-info">
        <div class="creator-item-header">
          <span class="creator-item-name">${escapeHtml(creation.characterName || creation.title || '이름 없음')}</span>
          <span class="creator-item-status ${statusClass}">${statusLabel}</span>
        </div>
        <div class="creator-item-plays-badge">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.04 2 11c0 2.76 1.36 5.22 3.5 6.83V22l4.09-2.25c.75.15 1.54.25 2.41.25 5.52 0 10-4.04 10-9S17.52 2 12 2z"/></svg>
          <span>${formatNumber(chatCount)}</span>
        </div>
      </div>
      <div class="creator-item-actions">
        <button class="creator-action-btn stats" data-action="openStats" data-id="${creation.id}" title="통계">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
          </svg>
        </button>
        <button class="creator-action-btn edit" data-action="openEditorById" data-id="${creation.id}" title="편집">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
        </button>
        <button class="creator-action-btn menu" data-action="showItemMenu" data-id="${creation.id}" title="메뉴">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/>
          </svg>
        </button>
      </div>
    </div>
  `;
}

function getStatusLabel(status) {
  switch (status) {
    case 'public': return '공개';
    case 'link': return '링크 공개';
    case 'private': return '비공개';
    case 'draft': return '임시저장';
    default: return status;
  }
}

// ============================================
// 캐릭터 상세 보기
// ============================================

export function openCreationDetail(creationId) {
  const currentCreations = getCurrentCreations();
  const creation = currentCreations.find(c => c.id === creationId);
  if (!creation) return;

  // 홈 탭의 상세 모달 함수 호출
  const nickname = getUserNickname();
  const contentData = {
    id: creation.id,
    title: creation.title || creation.characterName,
    characterName: creation.characterName || creation.title,
    imageUrl: creation.imageUrl,
    shortDesc: creation.shortDesc || creation.shortIntro || '',
    shortIntro: creation.shortIntro || creation.shortDesc || '',
    tags: creation.tags || creation.hashtags || [],
    authorName: creation.authorName || creation.authorNickname || nickname || '익명',
    authorNickname: creation.authorNickname || creation.authorName || nickname || '익명',
    chatCount: creation.chatCount || 0,
    likeCount: creation.likeCount || 0,
    type: creation.type || 'character',
    status: creation.status,
    character: creation.character,
    characters: creation.characters,
    world: creation.world,
    scenario: creation.scenario,
    worldSetting: creation.worldSetting,
    narrativeState: creation.narrativeState,
    firstMessage: creation.firstMessage,
    introImages: creation.introImages || [],
    images: creation.images || null,
    creatorId: getCurrentUser()?.uid,
    isMyCreation: true,
    safetyFilter: creation.safetyFilter,
    unfiltered: creation.unfiltered
  };
  showContentDetail(contentData);
}

// ============================================
// 유틸리티
// ============================================

function formatNumber(num) {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
}
